import React from "react";
import { formatToDateFull } from "../master/FormatToDateTime";
import {
  buildMarksheet,
  getRemark,
  nextClass,
  getSummaryItems,
  FILL_IN_ITEMS,
} from "./marksheetUtils";
import {
  useSetting,
  getLogo,
  schoolAddress,
  schoolContact,
  getStudentFields,
  MarksheetStyle,
  MarksTable,
  CoScholastic,
  GradeLegend,
} from "./marksheetShared";

// Marksheet 6: Editorial. Large light title, horizontal hairlines only
// (no boxes, no vertical lines), figures set as a ruled row.

const CSS = `
& { padding: 12mm 13mm; }
& .ms6-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; }
& .ms6-title { display: inline-block; padding-bottom: 6px; font-size: 32px; font-weight: 300; line-height: 1; letter-spacing: -.01em; border-bottom: 4px solid rgba(217,119,6,.9) !important; }
& .ms6-session { margin-top: 8px; font-size: 11px; opacity: .75; }
& .ms6-school { display: flex; align-items: center; gap: 10px; text-align: right; }
& .ms6-logo { width: 52px; height: 52px; object-fit: contain; }
& .ms6-name { font-size: 14px; font-weight: 700; }
& .ms6-meta { font-size: 9.5px; opacity: .75; }
& .ms6-rule { height: 0; margin: 10px 0; border-top: 2pt solid #000 !important; }

& .ms6-student { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px 18px; margin-bottom: 12px; }
& .ms6-field span { display: block; font-size: 9px; opacity: .65; }
& .ms6-field b { font-size: 11.5px; font-weight: 600; overflow-wrap: anywhere; }

& .ms-marks thead th, & .ms-cos thead th { padding: 5px 6px; text-align: center; font-weight: 700; border-top: .5pt solid #000 !important; border-bottom: 1pt solid #000 !important; }
& .ms-marks td, & .ms-cos td { padding: 5px 6px; text-align: center; font-variant-numeric: tabular-nums; border-bottom: .5pt solid rgba(0,0,0,.3) !important; }
& .ms-marks tfoot td { padding-top: 6px; font-weight: 800; border-top: 1pt solid #000 !important; border-bottom: 1pt solid #000 !important; }
& .ms-cos th:first-child, & .ms-cos td:first-child { text-align: left; }
& .ms-cos td { padding: 6px; }

& .ms6-figures { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; margin-top: 14px; }
& .ms6-figures--fill { grid-template-columns: repeat(3, 1fr); margin-top: 8px; }
& .ms6-fig { padding-top: 5px; border-top: 1pt solid #000 !important; }
& .ms6-fig span { display: block; font-size: 9px; opacity: .7; }
& .ms6-fig b { display: block; min-height: 20px; font-size: 16px; font-weight: 700; }

& .ms6-lower { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 14px; align-items: start; }
& .ms6-note { margin-bottom: 8px; }
& .ms6-note span { display: block; font-size: 9px; opacity: .65; }
& .ms6-note p { min-height: 20px; font-weight: 600; border-bottom: .5pt solid rgba(0,0,0,.3) !important; }

& .ms6-sign { display: flex; justify-content: space-between; gap: 24px; margin-top: 30px; }
& .ms6-sign div { flex: 1; text-align: center; padding-top: 4px; border-top: 1px solid #000 !important; }
& .ms-legend { margin-top: 12px; }
`;

export default function StudentMarksheet6({ data, batch, examDate }) {
  const setting = useSetting();
  const sheet = buildMarksheet(data, batch);
  const logo = getLogo(setting);
  const fields = getStudentFields(data, batch);
  const summary = getSummaryItems(sheet.overall);
  const remark = getRemark(sheet.overall.percentage, data?.student_id);

  return (
    <>
      <MarksheetStyle setting={setting} scope="ms6" css={CSS} />

      <div className="a4-page-marksheet ms6">
        <div className="ms6-head">
          <div>
            <div className="ms6-title">Report Card</div>
            <div className="ms6-session">
              Academic session {batch?.sessions?.session}
            </div>
          </div>
          <div className="ms6-school">
            <div>
              <div className="ms6-name">{setting?.name}</div>
              <div className="ms6-meta">{schoolAddress(setting)}</div>
              <div className="ms6-meta">{schoolContact(setting)}</div>
            </div>
            {logo && <img className="ms6-logo" src={logo} alt="" />}
          </div>
        </div>

        <div className="ms6-rule" />

        <div className="ms6-student">
          {fields.map((f) => (
            <div className="ms6-field" key={f.label}>
              <span>{f.label}</span>
              <b>{f.value || "-"}</b>
            </div>
          ))}
        </div>

        <MarksTable {...sheet} />

        <div className="ms6-figures">
          {summary.map((t) => (
            <div className="ms6-fig" key={t.label}>
              <span>{t.label}</span>
              <b>{t.value}</b>
            </div>
          ))}
        </div>
        <div className="ms6-figures ms6-figures--fill">
          {FILL_IN_ITEMS.map((label) => (
            <div className="ms6-fig" key={label}>
              <span>{label}</span>
              <b></b>
            </div>
          ))}
        </div>

        <div className="ms6-lower">
          <CoScholastic />
          <div>
            <div className="ms6-note">
              <span>Class teacher's remark</span>
              <p>{remark}</p>
            </div>
            <div className="ms6-note">
              <span>Promoted to class</span>
              <p>{nextClass(batch?.course?.title)}</p>
            </div>
            <div className="ms6-note">
              <span>Date</span>
              <p>{formatToDateFull(examDate)}</p>
            </div>
          </div>
        </div>

        <div className="ms6-sign">
          <div>Class Teacher</div>
          <div>Parent</div>
          <div>Principal</div>
        </div>

        <GradeLegend />
      </div>
    </>
  );
}
